import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { formatNumberDe } from "../../../utils/format";
import type { AdminSongSourceBreakdown } from "../../../graphql/types";

const SEGMENTS: { key: keyof AdminSongSourceBreakdown; modifier: string }[] = [
  { key: "chordProEditor", modifier: "chordpro" },
  { key: "openWith", modifier: "open-with" },
  { key: "onSongImport", modifier: "onsong" },
  { key: "songBookPlusImport", modifier: "songbook" },
];

/**
 * Where a user's songs came from, as a stacked mini-bar with a breakdown on
 * hover. The tooltip is positioned `fixed` from the trigger's bounding box —
 * the table clips its overflow for the rounded corners, so an absolutely
 * positioned one would be cut off. Counts are attributed by song author, unlike
 * the library column next to it, which only counts owned projects.
 */
export const UserOriginBar: React.FC<{ sources: AdminSongSourceBreakdown }> = ({ sources }) => {
  const { t } = useTranslation();
  const [anchor, setAnchor] = useState<{ top: number; left: number } | null>(null);

  const total = SEGMENTS.reduce((sum, segment) => sum + sources[segment.key], 0);

  const show = useCallback((event: React.SyntheticEvent<HTMLElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setAnchor({ top: rect.bottom + 8, left: rect.left });
  }, []);

  const hide = useCallback(() => setAnchor(null), []);

  if (total === 0) {
    return <span className="users-muted">—</span>;
  }

  return (
    <span
      className="users-origin"
      tabIndex={0}
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
    >
      <span className="users-origin-track">
        {SEGMENTS.map((segment) => {
          const value = sources[segment.key];
          if (value === 0) return null;
          return (
            <span
              key={segment.key}
              className={`users-origin-seg users-origin-seg--${segment.modifier}`}
              style={{ width: `${(value / total) * 100}%` }}
            />
          );
        })}
      </span>
      <span className="users-origin-total">{formatNumberDe(total)}</span>

      {anchor && (
        <span className="users-origin-tip" style={{ top: anchor.top, left: anchor.left }}>
          {SEGMENTS.map((segment) => (
            <span className="users-origin-tip-row" key={segment.key}>
              <span
                className={`users-origin-swatch users-origin-swatch--${segment.modifier}`}
              />
              <span className="users-origin-tip-label">
                {t(`users.origin.${segment.key}`)}
              </span>
              <strong>{formatNumberDe(sources[segment.key])}</strong>
            </span>
          ))}
          <span className="users-origin-tip-hint">{t("users.origin.hint")}</span>
        </span>
      )}
    </span>
  );
};
